/// <reference types="vite/client" />
import { 
  Monitor, 
  CreateMonitorRequest, 
  UpdateMonitorRequest, 
  MonitorStatus, 
  SystemStatus, 
  StatusOverview,
  MonitorGamification,
  LeaderboardEntry,
  GlobalStats,
  TestAlertResult,
  PingLog,
  User,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  UpdateProfileRequest,
  ChangePasswordRequest
} from '../types';

// Handle both absolute URLs and relative paths
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3501';
const API_PATH = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;

// Token management
let authToken: string | null = localStorage.getItem('authToken');

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

export const getAuthToken = () => authToken;

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Handle authentication errors
    if (response.status === 401) {
      setAuthToken(null);
      // Optionally redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

// Helper function to make authenticated requests
export function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  
  return headers;
}

// Monitor Management
export const monitorApi = {
  // Get all monitors
  async getMonitors(): Promise<Monitor[]> {
    const response = await fetch(`${API_PATH}/monitors`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: Monitor[]; count: number }>(response);
    return data.data;
  },

  // Create a new monitor
  async createMonitor(monitor: CreateMonitorRequest): Promise<Monitor> {
    const response = await fetch(`${API_PATH}/monitors`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(monitor),
    });
    const data = await handleResponse<{ success: boolean; data: Monitor }>(response);
    return data.data;
  },

  // Update a monitor
  async updateMonitor(id: number, monitor: UpdateMonitorRequest): Promise<Monitor> {
    const response = await fetch(`${API_PATH}/monitors/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(monitor),
    });
    const data = await handleResponse<{ success: boolean; data: Monitor }>(response);
    return data.data;
  },

  // Delete a monitor
  async deleteMonitor(id: number): Promise<void> {
    const response = await fetch(`${API_PATH}/monitors/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse(response);
  },

  // Get monitor status
  async getMonitorStatus(id: number): Promise<MonitorStatus> {
    const response = await fetch(`${API_PATH}/monitors/${id}/status`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: MonitorStatus }>(response);
    return data.data;
  },
};

// Status and Overview
export const statusApi = {
  // Get system status
  async getSystemStatus(): Promise<SystemStatus> {
    const response = await fetch(`${API_PATH}/status`);
    const data = await handleResponse<{ success: boolean; data: SystemStatus; timestamp: string }>(response);
    return data.data;
  },

  // Get status overview
  async getStatusOverview(): Promise<StatusOverview> {
    const response = await fetch(`${API_PATH}/status/overview`);
    const data = await handleResponse<{ success: boolean; data: StatusOverview; timestamp: string }>(response);
    return data.data;
  },

  // Get monitor history
  async getMonitorHistory(id: number, limit?: number, hours?: number, days?: number): Promise<PingLog[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (hours) params.append('hours', hours.toString());
    if (days) params.append('days', days.toString());

    const response = await fetch(`${API_PATH}/status/${id}/history?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: { ping_history: PingLog[]; count: number } }>(response);
    return data.data.ping_history;
  },
};

// Gamification API
export const gamificationApi = {
  // Get gamification data for a monitor
  async getMonitorGamification(id: number): Promise<MonitorGamification> {
    const response = await fetch(`${API_PATH}/gamification/monitor/${id}`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: MonitorGamification }>(response);
    return data.data;
  },

  // Get achievements for a monitor
  async getMonitorAchievements(id: number): Promise<{ achievements: any[]; count: number }> {
    const response = await fetch(`${API_PATH}/gamification/monitor/${id}/achievements`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: { achievements: any[]; count: number } }>(response);
    return data.data;
  },

  // Get global statistics
  async getGlobalStats(): Promise<GlobalStats> {
    const response = await fetch(`${API_PATH}/gamification/stats`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: GlobalStats }>(response);
    return data.data;
  },

  // Get leaderboard
  async getLeaderboard(): Promise<LeaderboardEntry[]> {
    const response = await fetch(`${API_PATH}/gamification/leaderboard`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: { leaderboard: LeaderboardEntry[] } }>(response);
    return data.data.leaderboard;
  },

  // Get recent achievements
  async getRecentAchievements(): Promise<{ achievements: any[]; count: number }> {
    const response = await fetch(`${API_PATH}/gamification/recent`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: { achievements: any[]; count: number } }>(response);
    return data.data;
  },
};

// Alerting API
export const alertingApi = {
  // Get alerting status
  async getAlertingStatus(): Promise<any> {
    const response = await fetch(`${API_PATH}/alerts/status`, {
      headers: getAuthHeaders()
    });
    const data = await handleResponse<any>(response);
    return data.data;
  },

  // Send test alerts
  async sendTestAlerts(): Promise<TestAlertResult> {
    const response = await fetch(`${API_PATH}/alerts/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await handleResponse<{ success: boolean; data: TestAlertResult }>(response);
    return data.data;
  },

  // Send test email only
  async sendTestEmail(): Promise<{ emailSent: boolean; message: string }> {
    const response = await fetch(`${API_PATH}/alerts/test-email`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}), // Send empty object to satisfy Fastify
    });
    const data = await handleResponse<{ success: boolean; data: { emailSent: boolean; message: string } }>(response);
    return data.data;
  },

  // Send test Discord alert
  async sendTestDiscord(): Promise<{ discordSent: boolean; message: string }> {
    const response = await fetch(`${API_PATH}/alerts/test-discord`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await handleResponse<{ success: boolean; data: { discordSent: boolean; message: string } }>(response);
    return data.data;
  },

  // Send test Slack alert
  async sendTestSlack(): Promise<{ slackSent: boolean; message: string }> {
    const response = await fetch(`${API_PATH}/alerts/test-slack`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await handleResponse<{ success: boolean; data: { slackSent: boolean; message: string } }>(response);
    return data.data;
  },

  // Update alerting config
  async updateConfig(config: any): Promise<any> {
    const response = await fetch(`${API_PATH}/alerts/config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(config),
    });
    const data = await handleResponse<{ success: boolean; data: any }>(response);
    return data.data;
  },
};

// Utility functions
export const formatUptime = (percentage: number | undefined | null): string => {
  if (percentage === undefined || percentage === null || isNaN(percentage)) {
    return '0.00%';
  }
  return `${percentage.toFixed(2)}%`;
};

export const formatResponseTime = (ms: number | undefined | null): string => {
  if (ms === undefined || ms === null || isNaN(ms)) {
    return '0ms';
  }
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

export const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Handle negative values (future dates or timezone issues)
  if (diffInSeconds < 0) {
    return 'Just now';
  }

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
};

export const getStatusColor = (isUp: boolean): string => {
  return isUp ? 'badge-success' : 'badge-error';
};

export const getStatusIcon = (isUp: boolean): string => {
  return isUp ? '🟢' : '🔴';
};

// Authentication API
export const authApi = {
  // Register a new user
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_PATH}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    const data = await handleResponse<{ success: boolean; data: AuthResponse }>(response);
    setAuthToken(data.data.token);
    return data.data;
  },

  // Login user
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_PATH}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    const data = await handleResponse<{ success: boolean; data: AuthResponse }>(response);
    setAuthToken(data.data.token);
    return data.data;
  },

  // Logout user
  async logout(): Promise<void> {
    const response = await fetch(`${API_PATH}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    await handleResponse<{ success: boolean }>(response);
    setAuthToken(null);
  },

  // Get current user profile
  async getProfile(): Promise<User> {
    const response = await fetch(`${API_PATH}/auth/profile`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: User }>(response);
    return data.data;
  },

  // Check authentication status
  async checkAuthStatus(): Promise<{ authenticated: boolean; user: User | null }> {
    const response = await fetch(`${API_PATH}/auth/status`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: { authenticated: boolean; user: User | null } }>(response);
    return data.data;
  },

  // Update user profile
  async updateProfile(userData: UpdateProfileRequest): Promise<User> {
    const response = await fetch(`${API_PATH}/auth/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    const data = await handleResponse<{ success: boolean; data: User }>(response);
    return data.data;
  },

  // Change password
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    const response = await fetch(`${API_PATH}/auth/change-password`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(passwordData),
    });
    await handleResponse<{ success: boolean }>(response);
  },

  // Check default admin status
  async checkDefaultAdminStatus(): Promise<{ success: boolean; data: { default_admin_updated: boolean } }> {
    const response = await fetch(`${API_PATH}/auth/check-default-admin-updated`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },

  // Finalize default admin setup
  async finalizeDefaultAdminSetup(): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_PATH}/auth/finalize-default-admin-setup`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return await handleResponse(response);
  },
}; 

export const userApi = {
  // List all users (admin only)
  async listUsers(): Promise<User[]> {
    const response = await fetch(`${API_PATH}/auth/users`, {
      headers: getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; data: User[] }>(response);
    return data.data;
  },

  // Update user info/role (admin only)
  async updateUser(id: number, update: Partial<User>): Promise<User> {
    const response = await fetch(`${API_PATH}/auth/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(update),
    });
    const data = await handleResponse<{ success: boolean; data: User }>(response);
    return data.data;
  },

  // Activate/deactivate user (admin only)
  async setUserActive(id: number, is_active: boolean): Promise<User> {
    const response = await fetch(`${API_PATH}/auth/users/${id}/active`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_active }),
    });
    const data = await handleResponse<{ success: boolean; data: User }>(response);
    return data.data;
  },

  // Admin reset user password
  async resetUserPassword(id: number, new_password: string): Promise<User> {
    const response = await fetch(`${API_PATH}/auth/users/${id}/reset-password`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ new_password }),
    });
    const data = await handleResponse<{ success: boolean; data: User }>(response);
    return data.data;
  },

  // Delete user (superadmin only)
  async deleteUser(id: number, transferToUserId?: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_PATH}/auth/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify(transferToUserId ? { transferToUserId } : {}),
    });
    return await handleResponse(response);
  },
}; 